// When a client's access is about to end, the sentence that tells them, or null.
//
// The owner's rule (9 October 2026): a client whose subscription is about to end is told, and
// offered their current program to keep, before the app closes to them. Two cases can be known
// in advance:
//   * they cancelled in Stripe and are using up what they paid for
//     (users.subscription_cancel_at_period_end and subscription_period_end, migration 074);
//   * their complimentary access has an end date in the next week and no paid subscription
//     takes over.
// A failed payment (owner, 24 October 2026, choice A): the member keeps access while Stripe retries
// the card, and is asked to update it.

const DAY = 24 * 60 * 60 * 1000;
export const COMPLIMENTARY_NOTICE_DAYS = 7;

const longDate = (date) => date.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });
const paying = (profile) => ['active', 'trialing', 'past_due'].includes(profile.subscription_status);

/**
 * @param {object | null} profile the signed-in person's users row
 * @param {Date} [now]
 * @returns {{ kind: 'payment_failed' | 'cancelled' | 'complimentary', endsOn?: Date, text: string } | null}
 */
export function subscriptionEndingNotice(profile, now = new Date()) {
  if (!profile || profile.role !== 'client') return null;

  if (profile.subscription_status === 'past_due') {
    return {
      kind: 'payment_failed',
      text: "Your last payment didn't go through. Please update your card to keep your membership: you can carry on as normal meanwhile.",
    };
  }

  if (profile.subscription_cancel_at_period_end && profile.subscription_period_end) {
    const endsOn = new Date(profile.subscription_period_end);
    if (!Number.isNaN(endsOn.getTime()) && endsOn > now) {
      return {
        kind: 'cancelled',
        endsOn,
        text: `Your subscription ends on ${longDate(endsOn)}. As a courtesy, you can keep a copy of your current program: download it before then.`,
      };
    }
  }

  if (profile.is_complimentary === true && profile.complimentary_until && !paying(profile)) {
    // A date with no time: access runs to the end of that day.
    const endsOn = new Date(`${String(profile.complimentary_until).slice(0, 10)}T23:59:59`);
    const daysLeft = (endsOn - now) / DAY;
    if (!Number.isNaN(endsOn.getTime()) && daysLeft >= 0 && daysLeft <= COMPLIMENTARY_NOTICE_DAYS) {
      return {
        kind: 'complimentary',
        endsOn,
        text: `Your complimentary access ends on ${longDate(endsOn)}. As a courtesy, you can keep a copy of your current program: download it before then.`,
      };
    }
  }

  return null;
}
